import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { BRAND } from '@/lib/config/brand'
import {
  DRIVE_ACCESS_COPY,
  isGoogleDriveAccessBlockedError,
} from '@/lib/google-drive-access'
import { testIds } from '@/lib/test-ids'

/** Popup landing page after Google Drive OAuth; notifies opener and closes the window. */
export function GoogleDriveOAuthDonePage() {
  const [closeAttempted, setCloseAttempted] = useState(false)

  const errorCode = useMemo(() => {
    if (typeof window === 'undefined') return null
    try {
      return new URLSearchParams(window.location.search).get('error')
    } catch {
      return null
    }
  }, [])

  const isError = Boolean(errorCode)
  const isBlocked = isGoogleDriveAccessBlockedError(errorCode)

  useEffect(() => {
    const origin = window.location.origin
    if (window.opener && !window.opener.closed) {
      try {
        if (isError) {
          window.opener.postMessage(
            {
              type: 'flownotes-google-drive-oauth-error',
              error: errorCode,
            },
            origin,
          )
        } else {
          window.opener.postMessage({ type: 'flownotes-google-drive-connected' }, origin)
        }
      } catch {
        // Cross-origin or closed; opener can still poll auth
      }
    }
    if (isError) {
      // Keep error page open so the user can read blocked copy.
      return
    }
    const timer = window.setTimeout(() => {
      setCloseAttempted(true)
      try {
        window.close()
      } catch {
        // ignore
      }
    }, 400)
    return () => window.clearTimeout(timer)
  }, [errorCode, isError])

  const hasOpener = typeof window !== 'undefined' && !!window.opener

  if (isError) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center bg-background p-6 text-center"
        data-testid={testIds.oauth.googleDriveDone}
      >
        <h1 className="text-xl font-semibold text-foreground mb-2">
          {isBlocked ? DRIVE_ACCESS_COPY.blockedTitle : 'Google Drive connection failed'}
        </h1>
        <p className="text-sm text-muted-foreground max-w-md mb-2">
          {isBlocked
            ? DRIVE_ACCESS_COPY.blockedBody
            : `Something went wrong connecting Google Drive (${errorCode}). Close this window and try again from ${BRAND.displayName}.`}
        </p>
        {isBlocked && (
          <p className="text-xs text-muted-foreground max-w-md mb-6">
            Return to {BRAND.displayName} and use Request access in the Drive panel.
          </p>
        )}
        <div className="flex flex-col sm:flex-row gap-3">
          {hasOpener && (
            <Button type="button" variant="secondary" onClick={() => window.close()}>
              Close window
            </Button>
          )}
          <Button asChild variant="default">
            <a href="/">Open {BRAND.displayName}</a>
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center bg-background p-6 text-center"
      data-testid={testIds.oauth.googleDriveDone}
    >
      <h1 className="text-xl font-semibold text-foreground mb-2">Google Drive connected</h1>
      <p className="text-sm text-muted-foreground max-w-md mb-6">
        {hasOpener
          ? `You can close this window and return to ${BRAND.displayName}. If it stays open, use Close window below.`
          : `Return to ${BRAND.displayName} to continue.`}
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        {hasOpener && (
          <Button type="button" variant="secondary" onClick={() => window.close()}>
            Close window
          </Button>
        )}
        <Button asChild variant="default">
          <a href="/">Open {BRAND.displayName}</a>
        </Button>
      </div>
      {closeAttempted && hasOpener && (
        <p className="text-xs text-muted-foreground mt-8 max-w-sm">
          If the window did not close automatically, your browser may require you to close it manually.
        </p>
      )}
    </div>
  )
}
